import {
  CarePlan,
  Condition,
  Location,
  Organization,
  Practitioner,
  Reference,
  Address,
} from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { buildDayjs, ISO_DATE, validateIsPastOrPresentSafe } from "@metriport/shared/common/date";
import dayjs from "dayjs";

export type CarePlanRowData = {
  id: string;
  description: string;
  practitionerName: string; // Maps to "practitioner" in V1
  locationDetails: string; // Maps to "location" in V1
  authorOrganizationDetails: string; // Maps to "author organization" in V1
  date: string;
  isUpcoming: boolean;
  rawDate: dayjs.Dayjs;
  rawEndDate?: dayjs.Dayjs;
  practitioner?: Practitioner;
  organization?: Organization;
  location?: Location;
  conditions?: Condition[];
  originalData: Smart<CarePlan>;
};

type DetailedCarePlan = {
  carePlan: Smart<CarePlan>;
  practitioner?: Practitioner;
  organization?: Organization;
  conditions?: Condition[];
  location?: Location;
  practitionerName?: string;
  organizationDetails?: string;
  locationDetails?: string;
};

export function getCarePlanRows({ bundle }: { bundle: FhirBundleSdk }): CarePlanRowData[] {
  try {
    // Export CarePlan resources using exportByType
    const exportedBundle = bundle.exportByType("CarePlan");
    const carePlanResources =
      exportedBundle.entry?.map(entry => entry.resource as Smart<CarePlan>) || [];
    const detailedCarePlans = getCarePlansAndRelatedResources(carePlanResources, bundle);
    return getCarePlanRowData(detailedCarePlans);
  } catch (error) {
    console.warn("Could not get CarePlans from bundle:", error);
    return [];
  }
}

function getCarePlansAndRelatedResources(
  carePlans: Smart<CarePlan>[],
  bundle: FhirBundleSdk
): DetailedCarePlan[] {
  return carePlans.map(cp => {
    const detailedCarePlan: DetailedCarePlan = {
      carePlan: cp,
    };

    const authorRef = cp.author;
    if (authorRef && authorRef.reference?.startsWith("Organization/")) {
      const organization = getReferenceResource<Organization>(authorRef, "Organization", bundle);
      detailedCarePlan.organization = organization;
      detailedCarePlan.organizationDetails = getOrganizationDetails(organization);
    }

    const activityDetails = cp.activity?.[0]?.detail;
    const practitionerRef = activityDetails?.performer?.find(p =>
      p.reference?.startsWith("Practitioner/")
    ) as Reference<Practitioner> | undefined;
    if (practitionerRef?.reference) {
      const practitioner = getReferenceResource<Practitioner>(
        practitionerRef,
        "Practitioner",
        bundle
      );
      detailedCarePlan.practitioner = practitioner;
      detailedCarePlan.practitionerName = getPractitionerDetails(practitioner);
    }

    const locationRef = activityDetails?.location;
    if (locationRef?.reference) {
      const location = getReferenceResource<Location>(locationRef, "Location", bundle);
      detailedCarePlan.location = location;
      detailedCarePlan.locationDetails = getLocationDetails(location);
    }

    const conditionRefs = cp.addresses;
    if (conditionRefs && conditionRefs.length > 0) {
      const conditions = conditionRefs.flatMap(ref => {
        const condition = getReferenceResource<Condition>(ref, "Condition", bundle);
        return condition ?? [];
      });
      detailedCarePlan.conditions = conditions;
    }

    return detailedCarePlan;
  });
}

function getOrganizationDetails(organization: Organization | undefined): string {
  return organization?.name ?? "-";
}

function getPractitionerDetails(practitioner: Practitioner | undefined): string {
  let detailsString = practitioner?.name?.[0]?.given?.join(" ");
  if (practitioner?.name?.[0]?.family) detailsString += ` ${practitioner?.name?.[0]?.family}`;

  const qualifications = practitioner?.qualification
    ?.map(q => q.code?.coding?.[0]?.display)
    .join(", ");

  if (qualifications) detailsString += ` (${qualifications})`;
  return detailsString ?? "-";
}

function getLocationDetails(location: Location | undefined): string {
  const name = location?.name ?? "-";
  const address = getAddressDetails(location?.address);
  return `${name} ${address ? `(${address})` : ""}`;
}

function getAddressDetails(addr: Address | undefined): string | undefined {
  if (!addr) return undefined;

  const { line, city, state, postalCode } = addr;
  const addressParts = [line?.join(", "), city, state, postalCode].filter(Boolean);
  return addressParts.length > 0 ? addressParts.join(", ") : undefined;
}

function getCarePlanRowData(detailedCarePlans: DetailedCarePlan[]): CarePlanRowData[] {
  return detailedCarePlans?.flatMap(d => {
    const carePlan = d.carePlan;
    const activityDetails = carePlan.activity?.[0]?.detail;
    const startDate = activityDetails?.scheduledPeriod?.start;
    const date = startDate ? buildDayjs(startDate) : undefined;
    const formattedStartDate = date?.format(ISO_DATE);
    if (!date || !formattedStartDate) return [];

    const endDate = activityDetails?.scheduledPeriod?.end;
    const end = endDate ? buildDayjs(endDate) : undefined;

    return {
      id: carePlan.id ?? "-",
      description: getCarePlanDescription(carePlan),
      practitionerName: d.practitionerName ?? "-",
      locationDetails: d.locationDetails ?? "-",
      authorOrganizationDetails: d.organizationDetails ?? "-",
      date: formattedStartDate,
      isUpcoming: !validateIsPastOrPresentSafe(formattedStartDate),
      rawDate: date,
      rawEndDate: end,
      practitioner: d.practitioner,
      organization: d.organization,
      location: d.location,
      conditions: d.conditions,
      originalData: carePlan,
    };
  });
}

function getCarePlanDescription(carePlan: CarePlan) {
  const activityDetails = carePlan.activity?.[0]?.detail;
  return `${carePlan.description}${
    activityDetails?.description ? ` - ${activityDetails.description}` : ""
  }`;
}

function getReferenceResource<Resource>(
  reference: Reference | undefined,
  resourceType: string,
  bundle: FhirBundleSdk
): Resource | undefined {
  if (!reference || !reference.reference) {
    return undefined;
  }

  const resourceId = reference.reference.split("/")[1];
  if (!resourceId) {
    return undefined;
  }

  try {
    // Try to get the resource using the bundle's getter methods
    const exportedBundle = bundle.exportByType(
      resourceType as "Organization" | "Practitioner" | "Location" | "Condition"
    );
    const resource = exportedBundle.entry?.find(entry => entry.resource?.id === resourceId)
      ?.resource as Resource;

    return resource;
  } catch (error) {
    console.warn(`Could not get ${resourceType} resource with id ${resourceId}:`, error);
    return undefined;
  }
}
